import { Checkbox, Input } from "@heroui/react";
import {
  Calendar,
  CheckCircle,
  Circle,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useCallback, useMemo, useState } from "react";
import {
  LegalProcedure,
  PartyRole,
  ProcedureDate,
  ProcedureDateType,
  PROCEDURE_DATE_TYPE_LABELS,
  getDefaultTimelineTemplate,
} from "shared/types/legal-procedure";
import { ProcedureDateForm } from "./ProcedureDateForm";

interface ProcedureTimelineProps {
  procedure: LegalProcedure;
  onAddDate: (data: { type: ProcedureDateType; date: string; remarks?: string }) => Promise<void>;
  onToggleDateCompleted: (dateItem: ProcedureDate) => void;
  onDeleteDate: (dateId: number) => void;
  onToggleConclusieTermijnen: (value: boolean) => void;
  isSavingDate: boolean;
}

interface TimelineSlot {
  key: string;
  type: ProcedureDateType;
  label: string;
  dateRecord: ProcedureDate | null;
  isPredefined: boolean;
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString("nl-BE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const ProcedureTimeline = ({
  procedure,
  onAddDate,
  onToggleDateCompleted,
  onDeleteDate,
  onToggleConclusieTermijnen,
  isSavingDate,
}: ProcedureTimelineProps) => {
  const [insertAfterIndex, setInsertAfterIndex] = useState<number | null>(null);
  const [settingDateForSlot, setSettingDateForSlot] = useState<string | null>(null);
  const [slotDateValue, setSlotDateValue] = useState("");
  const [extraConclusieCount, setExtraConclusieCount] = useState(0);

  // Count existing conclusietermijnen to know if we need extra slots beyond 3
  const existingConclusieCount = useMemo(() => {
    return procedure.procedureDates.filter(
      (d) =>
        d.type === ProcedureDateType.CONCLUSIETERMIJN_EISER ||
        d.type === ProcedureDateType.CONCLUSIETERMIJN_VERWEERDER
    ).length;
  }, [procedure.procedureDates]);

  const neededExtra = Math.max(0, existingConclusieCount - 3, extraConclusieCount);

  // Build timeline slots by merging template with existing records
  const timelineSlots = useMemo(() => {
    const template = getDefaultTimelineTemplate(
      procedure.partyRole || PartyRole.EISENDE,
      procedure.hasConclusieTermijnen ?? false,
      neededExtra
    );

    // Group existing dates by type for matching
    const datesByType = new Map<ProcedureDateType, ProcedureDate[]>();
    for (const pd of procedure.procedureDates) {
      const list = datesByType.get(pd.type) || [];
      list.push(pd);
      datesByType.set(pd.type, list);
    }
    // Sort each group by date
    datesByType.forEach((list) => list.sort((a, b) => a.date.localeCompare(b.date)));

    // Track consumed indices per type
    const consumedIndex = new Map<ProcedureDateType, number>();

    const slots: TimelineSlot[] = template.map((type, i) => {
      const idx = consumedIndex.get(type) || 0;
      const available = datesByType.get(type) || [];
      const matched = available[idx] || null;
      if (matched) consumedIndex.set(type, idx + 1);

      return {
        key: `template-${i}-${type}`,
        type,
        label: PROCEDURE_DATE_TYPE_LABELS[type],
        dateRecord: matched,
        isPredefined: true,
      };
    });

    // Add any unmatched custom dates (ZITTING, PLEIDOOI, ANDERE, etc.)
    const matchedIds = new Set(slots.filter((s) => s.dateRecord).map((s) => s.dateRecord!.id));
    const unmatchedDates = procedure.procedureDates
      .filter((pd) => !matchedIds.has(pd.id))
      .sort((a, b) => a.date.localeCompare(b.date));

    for (const pd of unmatchedDates) {
      // Insert custom dates at the appropriate position based on date
      let insertIdx = slots.length;
      for (let i = 0; i < slots.length; i++) {
        const slotDate = slots[i].dateRecord?.date;
        if (slotDate && pd.date < slotDate) {
          insertIdx = i;
          break;
        }
      }
      slots.splice(insertIdx, 0, {
        key: `custom-${pd.id}`,
        type: pd.type,
        label: PROCEDURE_DATE_TYPE_LABELS[pd.type] || pd.type,
        dateRecord: pd,
        isPredefined: false,
      });
    }

    return slots;
  }, [procedure, neededExtra]);

  const handleSetDateForSlot = useCallback(
    async (slot: TimelineSlot) => {
      if (!slotDateValue) return;
      await onAddDate({
        type: slot.type,
        date: slotDateValue,
      });
      setSettingDateForSlot(null);
      setSlotDateValue("");
    },
    [slotDateValue, onAddDate]
  );

  const handleInsertCustom = useCallback(
    async (data: { type: ProcedureDateType; date: string; remarks?: string }) => {
      await onAddDate(data);
      setInsertAfterIndex(null);
    },
    [onAddDate]
  );

  return (
    <div className="flex flex-col gap-1">
      {/* Conclusietermijnen toggle */}
      <div className="flex items-center gap-2 mb-2 p-2 bg-neutral-50 rounded-lg">
        <Checkbox
          isSelected={procedure.hasConclusieTermijnen ?? false}
          onValueChange={onToggleConclusieTermijnen}
          size="sm"
        >
          <span className="text-xs font-medium">Conclusietermijnen</span>
        </Checkbox>
        <span className="text-[11px] text-neutral-secondary-mid">
          (aanvinken als er conclusies gewisseld worden)
        </span>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-neutral-200" />

        <div className="flex flex-col">
          {timelineSlots.map((slot, index) => (
            <div key={slot.key}>
              {/* Timeline item */}
              <div className="flex items-start gap-3 group relative">
                {/* Circle indicator */}
                <div className="flex-shrink-0 relative z-10 mt-0.5">
                  {slot.dateRecord?.completed ? (
                    <CheckCircle
                      weight="fill"
                      size={22}
                      className="text-green-500 bg-white rounded-full"
                    />
                  ) : slot.dateRecord ? (
                    <CheckCircle
                      weight="duotone"
                      size={22}
                      className="text-moss-primary bg-white rounded-full"
                    />
                  ) : (
                    <Circle
                      weight="regular"
                      size={22}
                      className="text-neutral-400 bg-white rounded-full"
                    />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-3">
                  <div className="flex items-center gap-2 min-h-[28px]">
                    <span
                      className={`text-xs font-medium ${
                        slot.dateRecord
                          ? slot.dateRecord.completed
                            ? "text-green-700 line-through"
                            : "text-neutral-primary"
                          : "text-neutral-secondary-mid"
                      }`}
                    >
                      {slot.label}
                    </span>

                    {slot.dateRecord ? (
                      <>
                        <span className="text-xs text-neutral-secondary-mid">
                          {formatDate(slot.dateRecord.date)}
                        </span>
                        <Checkbox
                          isSelected={slot.dateRecord.completed}
                          onValueChange={() => onToggleDateCompleted(slot.dateRecord!)}
                          size="sm"
                          className="ml-1"
                        />
                        <button
                          onClick={() => onDeleteDate(slot.dateRecord!.id)}
                          className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-neutral-secondary-mid hover:text-red-500 transition-opacity"
                        >
                          <Trash size={12} />
                        </button>
                      </>
                    ) : slot.isPredefined ? (
                      <>
                        {settingDateForSlot === slot.key ? (
                          <div className="flex items-center gap-1">
                            <Input
                              type="date"
                              size="sm"
                              value={slotDateValue}
                              onValueChange={setSlotDateValue}
                              className="w-[140px]"
                            />
                            <ButtonNew
                              variant="primaryMoss"
                              label="OK"
                              onClick={() => handleSetDateForSlot(slot)}
                              isDisabled={isSavingDate || !slotDateValue}
                            />
                            <ButtonNew
                              variant="secondaryNeutral"
                              label="X"
                              onClick={() => {
                                setSettingDateForSlot(null);
                                setSlotDateValue("");
                              }}
                            />
                          </div>
                        ) : (
                          <button
                            onClick={() => {
                              setSettingDateForSlot(slot.key);
                              setSlotDateValue("");
                            }}
                            className="flex items-center gap-1 text-[11px] text-neutral-secondary-mid hover:text-moss-primary transition-colors"
                          >
                            <Calendar size={12} />
                            <span>Datum instellen</span>
                          </button>
                        )}
                      </>
                    ) : null}
                  </div>

                  {slot.dateRecord?.remarks && (
                    <p className="text-[11px] text-neutral-secondary-mid mt-0.5">
                      {slot.dateRecord.remarks}
                    </p>
                  )}
                </div>
              </div>

              {/* Insert point between items */}
              {insertAfterIndex === index ? (
                <div className="ml-8 mb-2">
                  <ProcedureDateForm
                    onSubmit={handleInsertCustom}
                    onCancel={() => setInsertAfterIndex(null)}
                    isSaving={isSavingDate}
                  />
                </div>
              ) : (
                <div className="ml-6 mb-0.5">
                  <button
                    onClick={() => setInsertAfterIndex(index)}
                    className="flex items-center gap-1 text-[10px] text-neutral-secondary-mid hover:text-moss-primary opacity-0 group-hover:opacity-100 hover:opacity-100 transition-opacity py-0.5"
                    style={{ opacity: 0 }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                  >
                    <Plus size={10} />
                    <span>Gebeurtenis invoegen</span>
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Add more conclusietermijnen */}
      {procedure.hasConclusieTermijnen && (
        <ButtonNew
          variant="secondaryNeutral"
          icon={<Plus size={12} />}
          label="Conclusietermijn toevoegen"
          onClick={() => setExtraConclusieCount((prev) => prev + 1)}
        />
      )}

      {/* Add custom event at the end */}
      {insertAfterIndex === null && (
        <ButtonNew
          variant="secondaryMoss"
          icon={<Plus size={12} />}
          label="Gebeurtenis toevoegen"
          onClick={() => setInsertAfterIndex(timelineSlots.length - 1)}
        />
      )}
    </div>
  );
};
